import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarCheck, CalendarClock, Loader2, MailWarning, Send } from 'lucide-react';
import type { KickoffAck } from '../../services/kickoff/kickoffTypes';
import { benachrichtigeKickoff, ladeKickoffsFuerProjekte } from '../../services/kickoff/kickoffService';
import { describeError } from '../../services/errorMessage';
import { kickoffStatusZeilen, type KickoffKennzeichen } from './kickoffStatusLogic';

/**
 * Anzeige der Kalender-Quittung im Hub (docs/projekt-kickoff-plan.md §4.4)
 * und das Kennzeichen „Kalender ✔ / offen" für die Admin-Projektliste
 * (Daten dazu: useKickoffKennzeichen.ts).
 *
 * FAIL-OPEN überall: fehlt die Migration (Nr. 146/147), gibt es keine Zeile
 * (Altprojekt, Übungsprojekt) oder scheitert das Laden, erscheint NICHTS —
 * die Oberfläche sieht dann aus wie vor dem Kickoff. Ein Fehler hier darf
 * weder die Stammdaten noch die Projektliste blockieren.
 *
 * Texte und Tageszählung stehen in kickoffStatusLogic.ts (ohne DOM getestet).
 */

interface KickoffStatusProps {
    projectId: string | null | undefined;
    /** Besitzer oder Admin: darf die Termine erneut per Mail senden. */
    darfErneutSenden: boolean;
    /** Anzeigename zu einer profiles.id; ohne Treffer bleibt die Zeile namenlos. */
    nameFuer?: (recipientId: string) => string | undefined;
}

/** Noch nicht gespeicherte Projekte tragen diese Platzhalter-Id. */
const istEchteProjektId = (id: string | null | undefined): id is string => !!id && id !== 'temp-id';

const KEINE: KickoffAck[] = [];

export const KickoffStatus: React.FC<KickoffStatusProps> = ({ projectId, darfErneutSenden, nameFuer }) => {
    // Stand und Meldung tragen das Projekt, für das sie gelten: wechselt der
    // Hub das Projekt, zeigt die Anzeige bis zum neuen Laden nichts statt
    // der Zeilen des vorigen Projekts.
    const [stand, setStand] = useState<{ fuer: string; acks: KickoffAck[] } | null>(null);
    const [sendetFuer, setSendetFuer] = useState<string | null>(null);
    const [meldung, setMeldung] = useState<{ fuer: string; text: string; fehler: boolean } | null>(null);

    const laden = useCallback(async (): Promise<void> => {
        if (!istEchteProjektId(projectId)) return;
        let rows: KickoffAck[] = [];
        try {
            rows = await ladeKickoffsFuerProjekte([projectId]);
        } catch (e) {
            // Echte Lesefehler (Netz, RLS) — Schema-Fehler fängt der Dienst
            // selbst. Anzeige entfällt, die Stammdaten bleiben bedienbar.
            console.warn('Kickoff-Status nicht ladbar —', describeError(e));
        }
        setStand({ fuer: projectId, acks: rows });
    }, [projectId]);

    useEffect(() => {
        void laden();
    }, [laden]);

    const acks = stand && stand.fuer === projectId ? stand.acks : KEINE;
    const zeilen = useMemo(() => kickoffStatusZeilen(acks), [acks]);

    if (!istEchteProjektId(projectId) || zeilen.length === 0) return null;
    const pid = projectId;

    const erneutSenden = async (recipientId: string) => {
        setSendetFuer(recipientId);
        setMeldung(null);
        try {
            // Nur an DIESEN Empfänger: wer schon bestätigt hat, bekommt keine
            // zweite Mail, nur weil ein Kollege noch offen ist. Wirft nie.
            const ergebnisse = await benachrichtigeKickoff([pid], { erneut: true, nurEmpfaenger: recipientId });
            const fehler = ergebnisse.filter(r => !r.ok);
            if (ergebnisse.length === 0) {
                setMeldung({ fuer: pid, text: 'Nichts zu senden — die Termine sind bereits bestätigt.', fehler: false });
            } else if (fehler.length > 0) {
                setMeldung({ fuer: pid, text: `Senden fehlgeschlagen: ${fehler.map(r => r.error || 'unbekannter Fehler').join('; ')}`, fehler: true });
            } else {
                const an = ergebnisse.map(r => r.email).filter(Boolean).join(', ');
                setMeldung({ fuer: pid, text: an ? `Termine erneut gesendet an ${an}.` : 'Termine erneut gesendet.', fehler: false });
            }
        } finally {
            // Mailstatus (gesendet/Fehler) steht danach in der Zeile — neu lesen.
            await laden();
            setSendetFuer(null);
        }
    };

    return (
        <div className="bg-white/70 rounded-xl border border-slate-200 p-3 space-y-2">
            <h4 className="text-[11px] font-black uppercase tracking-wide text-slate-500 flex items-center gap-1.5">
                <CalendarCheck className="w-3.5 h-3.5" /> Termine im Kalender
            </h4>
            {zeilen.map(z => {
                const name = nameFuer?.(z.recipientId);
                const farbe = z.bestaetigt ? 'text-emerald-700' : z.ueberfaellig ? 'text-red-600' : 'text-amber-700';
                return (
                    <div key={z.id} className="text-xs space-y-1">
                        {(name || z.bereichText) && (
                            <div className="flex items-center gap-1.5 text-slate-700 font-bold">
                                {name && <span className="truncate">{name}</span>}
                                {z.bereichText && (
                                    <span className="text-[10px] font-black uppercase px-1.5 py-0.5 rounded border bg-slate-50 text-slate-500 border-slate-200">{z.bereichText}</span>
                                )}
                            </div>
                        )}
                        <div className={`flex items-center gap-1.5 font-bold ${farbe}`}>
                            {z.bestaetigt ? <CalendarCheck className="w-3.5 h-3.5 shrink-0" /> : <CalendarClock className="w-3.5 h-3.5 shrink-0" />}
                            <span>{z.statusText}</span>
                        </div>
                        {z.mailFehlerText && (
                            <div className="flex items-start gap-1.5 text-red-600">
                                <MailWarning className="w-3.5 h-3.5 shrink-0 mt-px" />
                                <span>{z.mailFehlerText}</span>
                            </div>
                        )}
                        {/* Nur offene Zeilen: der Versand nimmt bestätigte ohnehin nicht mit. */}
                        {darfErneutSenden && !z.bestaetigt && (
                            <button
                                type="button"
                                onClick={() => void erneutSenden(z.recipientId)}
                                disabled={sendetFuer !== null}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-slate-200 bg-white text-[11px] font-bold text-slate-600 hover:border-blue-300 hover:text-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {sendetFuer === z.recipientId ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                                Termine erneut senden
                            </button>
                        )}
                    </div>
                );
            })}
            {meldung && meldung.fuer === pid && (
                <p className={`text-[11px] ${meldung.fehler ? 'text-red-600 font-bold' : 'text-slate-500'}`}>{meldung.text}</p>
            )}
        </div>
    );
};

/** Kennzeichen für die Admin-Projektliste; ohne Daten nichts. */
export const KickoffKennzeichenBadge: React.FC<{ kennzeichen: KickoffKennzeichen | undefined }> = ({ kennzeichen }) => {
    if (!kennzeichen) return null;
    const stil = kennzeichen.bestaetigt
        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
        : kennzeichen.rot
            ? 'bg-red-50 text-red-700 border-red-200'
            : 'bg-amber-50 text-amber-700 border-amber-200';
    return (
        <span title={kennzeichen.titel} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[11px] font-bold whitespace-nowrap ${stil}`}>
            {kennzeichen.bestaetigt ? <CalendarCheck className="w-3 h-3" /> : <CalendarClock className="w-3 h-3" />}
            {kennzeichen.text}
        </span>
    );
};
